import { FileText, Presentation, Send, ShieldCheck } from "lucide-react";

import type { AssistantTurn, EscalationRequest, GenerationResult } from "@/lib/api";
import { cn } from "@/lib/utils";

import { escalationTitle, versionSummary } from "./utils";
import type { RailView } from "./views";

interface ConversationElementsProps {
  turns: AssistantTurn[];
  pageResults: Record<string, GenerationResult>;
  escalations: EscalationRequest[];
  view: RailView;
  onSelect: (view: RailView) => void;
}

/**
 * Everything this conversation produced, listed beside it.
 *
 * The left rail is the app's furniture — the rule book, the archive. This panel
 * is the opposite: only what *this* conversation made, in the order it made it,
 * so a requestor can see the deck they built two questions ago without
 * scrolling back through the thread to find it.
 */
export function ConversationElements({
  turns,
  pageResults,
  escalations,
  view,
  onSelect,
}: ConversationElementsProps) {
  // A turn contributes a file once it has been built — a proposed deck plan is
  // not a file yet, and listing it as one would overstate what exists.
  const files = turns
    .map((turn) => ({ turn, result: pageResults[turn.turn_id] ?? turn.result ?? undefined }))
    .filter((entry): entry is { turn: AssistantTurn; result: GenerationResult } =>
      Boolean(entry.result),
    );

  const reports = files.filter((entry) => (entry.result.guardrail_report ?? []).length > 0);

  const turnIds = new Set(turns.map((turn) => turn.turn_id));
  const drafts = escalations.filter(
    (escalation) =>
      turns.some((turn) => turn.escalation_draft?.id === escalation.id) || turnIds.size === 0,
  );

  const empty = files.length === 0 && drafts.length === 0;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header className="border-border shrink-0 border-b px-4 py-3">
        <h2 className="type-label text-muted">In this conversation</h2>
      </header>

      {empty ? (
        <p className="text-muted px-4 py-4 text-xs leading-5">
          Nothing built yet. Decks, PDFs and drafted briefs appear here as you make them, so you can
          reopen one without scrolling back.
        </p>
      ) : (
        <div className="space-y-5 px-3 py-4">
          <Section icon={ShieldCheck} label="Guardrails" count={reports.length}>
            {reports.map(({ turn, result }) => (
              <ElementLink
                key={`report-${turn.turn_id}`}
                label={result.filename}
                meta={`${(result.guardrail_report ?? []).length} rules recorded`}
                selected={view.kind === "run-report" && view.turnId === turn.turn_id}
                onClick={() => onSelect({ kind: "run-report", turnId: turn.turn_id })}
              />
            ))}
          </Section>

          <Section icon={Presentation} label="Files" count={files.length}>
            {files.map(({ turn, result }) => (
              <ElementLink
                key={`file-${turn.turn_id}`}
                label={result.filename}
                // One description of a file, shared with the rail's version
                // list — a set of social ads is several files, and the old
                // page count called it "0 pages".
                meta={`${versionSummary(result)} · ${result.spec.target_language}`}
                selected={view.kind === "file" && view.turnId === turn.turn_id}
                onClick={() => onSelect({ kind: "file", turnId: turn.turn_id })}
              />
            ))}
          </Section>

          <Section icon={Send} label="Drafts" count={drafts.length}>
            {drafts.map((escalation) => (
              <ElementLink
                key={escalation.id}
                label={escalationTitle(escalation)}
                meta={escalation.status ?? "drafted"}
                selected={view.kind === "draft" && view.id === escalation.id}
                onClick={() => onSelect({ kind: "draft", id: escalation.id })}
              />
            ))}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({
  icon: Icon,
  label,
  count,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  if (count === 0) {
    return null;
  }
  return (
    <section className="space-y-1">
      <div className="flex items-center gap-2 px-2 pb-1">
        <Icon className="text-muted h-3.5 w-3.5 shrink-0" />
        <h3 className="type-label text-muted flex-1">{label}</h3>
        <span className="text-faint text-[11px] tabular-nums">{count}</span>
      </div>
      {children}
    </section>
  );
}

function ElementLink({
  label,
  meta,
  selected,
  onClick,
}: {
  label: string;
  meta: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? "page" : undefined}
      className={cn(
        "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
        selected ? "bg-daylight-subtle" : "hover:bg-surface-subtle",
      )}
    >
      <FileText className="text-faint mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-xs",
            selected ? "text-foreground font-semibold" : "text-foreground/85",
          )}
        >
          {label}
        </span>
        <span className="text-muted block truncate text-[11px]">{meta}</span>
      </span>
    </button>
  );
}
